import { useQuery } from '@apollo/client'
import { Search } from '@mui/icons-material'
import {
    Box,
    Button,
    Card,
    FormControlLabel,
    InputAdornment,
    Stack,
    Switch,
    TextField,
    Typography,
} from '@mui/material'
import { useState } from 'react'
import { AdminGetRecruiters } from 'recruiter/graphql/admin.gql'
import useToast from 'shared/components/Toast/useToast'
import { usePageTitle } from 'shared/hooks/usePageTitle'
import { getPxAsRem } from 'shared/utils'
import RecruiterModal, { type RecruiterToEdit } from './RecruiterModal'
import RecruitersTable from './RecruitersTable'

export default function AdminDashboard() {
    const toast = useToast()
    const [modalOpen, setModalOpen] = useState(false)
    const [editingRecruiter, setEditingRecruiter] =
        useState<RecruiterToEdit | null>(null)
    const [searchValue, setSearchValue] = useState('')
    const [showInactive, setShowInactive] = useState(false)

    usePageTitle('Admin · Manage Recruiters')

    const { data, refetch } = useQuery(AdminGetRecruiters, {
        onError: (error) => {
            toast({
                title: 'Error',
                severity: 'error',
                message: error.message,
            })
        },
    })

    const handleAdd = () => {
        setEditingRecruiter(null)
        setModalOpen(true)
    }

    const handleEdit = (recruiter: RecruiterToEdit) => {
        setEditingRecruiter(recruiter)
        setModalOpen(true)
    }

    const handleModalClose = () => {
        setModalOpen(false)
        setEditingRecruiter(null)
    }

    return (
        <>
            <Stack
                direction="column"
                spacing={2}
                sx={{
                    padding: getPxAsRem(16, 24),
                    height: '100%',
                    overflow: 'hidden',
                }}
            >
                <Typography variant="h4">Manage Recruiters</Typography>
                <Card
                    sx={{
                        padding: getPxAsRem(16),
                        flex: 1,
                        overflow: 'hidden',
                        borderRadius: getPxAsRem(8),
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Stack
                        direction="column"
                        spacing={2}
                        sx={{ flex: 1, overflow: 'hidden' }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                flexShrink: 0,
                            }}
                        >
                            <Stack
                                direction="row"
                                spacing={2}
                                alignItems="center"
                            >
                                <TextField
                                    onChange={(e) =>
                                        setSearchValue(e?.target?.value ?? '')
                                    }
                                    variant="standard"
                                    value={searchValue}
                                    placeholder="Search Name"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Search />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={showInactive}
                                            onChange={(_, checked) =>
                                                setShowInactive(checked)
                                            }
                                            size="small"
                                        />
                                    }
                                    label="Show Inactive"
                                />
                            </Stack>
                            <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                onClick={handleAdd}
                            >
                                Add Recruiter
                            </Button>
                        </Box>
                        <Box sx={{ flex: 1, overflow: 'auto' }}>
                            <RecruitersTable
                                data={data}
                                searchInput={searchValue}
                                showInactive={showInactive}
                                refetch={refetch}
                                onEdit={handleEdit}
                            />
                        </Box>
                    </Stack>
                </Card>
            </Stack>
            <RecruiterModal
                open={modalOpen}
                recruiter={editingRecruiter}
                onCancel={handleModalClose}
                refetch={refetch}
            />
        </>
    )
}
